import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useDropzone } from 'react-dropzone';
import { ticketService } from '../../services/questService';
import { MAX_ATTACHMENT_BYTES, tooLargeAttachmentMessage } from '../../utils/attachmentSize';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Б';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const AttachmentUploadDialog = ({ open, onClose, questId, onUploadSuccess }) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileSizeWarning, setFileSizeWarning] = useState('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (acceptedFiles?.length) {
        setFiles(prev => [...prev, ...acceptedFiles]);
      }
      setFileSizeWarning(tooLargeAttachmentMessage(fileRejections));
      setError(null);
    },
    maxSize: MAX_ATTACHMENT_BYTES,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp'],
      'application/*': ['.pdf', '.doc', '.docx', '.txt'],
    },
    multiple: true,
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Выберите хотя бы один файл');
      return;
    }

    if (!questId) {
      setError('Не указан идентификатор заявки');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadedIds = [];
      for (const file of files) {
        const uploadResult = await ticketService.uploadAttachment(questId, file);
        uploadedIds.push(uploadResult.attachmentId ?? uploadResult.fileId);
      }

      if (onUploadSuccess) {
        onUploadSuccess(uploadedIds);
      }

      setFiles([]);
      setFileSizeWarning('');
      // Диалог закроет родительский компонент через onClose
    } catch (err) {
      console.error('Ошибка загрузки вложений:', err);
      setError(err.message || 'Не удалось загрузить файлы. Попробуйте снова.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setError(null);
    setFileSizeWarning('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Добавить вложения
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {fileSizeWarning && (
          <Alert severity="warning" onClose={() => setFileSizeWarning('')} sx={{ mb: 2, borderRadius: 2 }}>
            {fileSizeWarning}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          {...getRootProps()}
          sx={{
            p: 2.5,
            textAlign: 'center',
            cursor: 'pointer',
            border: '2px dashed',
            borderRadius: 2,
            borderColor: isDragActive ? 'primary.main' : 'divider',
            bgcolor: isDragActive ? 'action.hover' : 'grey.50',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 36, color: isDragActive ? 'primary.main' : 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {isDragActive ? 'Отпустите файлы здесь' : 'Перетащите файлы или нажмите для выбора'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.disabled' }}>
            JPG, PNG, GIF, PDF, DOC, DOCX, TXT (макс. 20 МБ)
          </Typography>
        </Box>

        {files.length > 0 && (
          <Stack spacing={0.75} sx={{ mt: 2 }}>
            {files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                  <AttachFileIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                  <Tooltip title={file.name}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {file.name}
                    </Typography>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => removeFile(index)} sx={{ color: 'error.main' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isUploading}
          sx={{ textTransform: 'none' }}
        >
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isUploading || files.length === 0}
          startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isUploading ? 'Загрузка...' : 'Загрузить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachmentUploadDialog;